/**
 * ai-trade-journal.js — AI-Powered Trade Journal for SPEKTRX
 *
 * Provides LLM-generated trade reviews for closed trades and manual journal entries.
 * Integrates into the existing Trade Journal section on the Trades page.
 *
 * Exports:
 * - aiReviewTrade(tradeId)       — Generate AI review for a specific closed trade
 * - addJournalEntry(tradeId)     — Open manual journal entry editor
 * - saveJournalEntry(tradeId)    — Save a manual journal entry
 * - deleteJournalEntry(tradeId, entryId) — Delete a journal entry
 * - toggleJournalExpand(tradeId) — Toggle expand/collapse of journal entries
 * - renderJournalEntries(tradeId) — Render journal entries for a trade row
 * - getJournalBadge(trade)       — Return badge HTML if trade has journal entries
 * - offerPostExitReview(trade)   — Show toast offering AI review after trade exit
 */

import { S, save, stampItem } from '../core/state.js';
import { chatJSON, isAIConfigured, getAIConfig } from '../core/ai.js';
import { esc, uid, now, promptSafe, realizedForTrade } from '../core/utils.js';
import { toast } from '../ui/layout.js';

// ─── CONSTANTS ────────────────────────────────────────────────────

const REVIEW_COOLDOWN_MS = 30 * 1000; // 30s between AI reviews
let _lastReviewTs = 0;
let _loadingTradeId = null;

// Trade IDs with expanded journal sections
const _expanded = new Set();

// ─── AI TRADE REVIEW ──────────────────────────────────────────────

/**
 * Build trade context for the LLM prompt.
 */
function _buildTradeContext(trade) {
  const cur = trade.currency || 'USD';
  const exits = trade.exits || [];
  // One rule (`utils.realizedForTrade`); this one feeds an LLM prompt, so a row belonging to
  // another deposit would become a sentence about a trade that never happened.
  const related = realizedForTrade(trade, S.realized, S.trades);

  const totalPnl = related.reduce((s, r) => s + (r.pnlUSD || 0), 0);
  const avgExitQty = related.reduce((s, r) => s + r.qty, 0);
  const avgExit = avgExitQty > 0 ? related.reduce((s, r) => s + r.exit * r.qty, 0) / avgExitQty : 0;

  const openDate = trade.openDate || trade.date;
  const closeDate = trade.closeDate || (exits.length ? exits[exits.length - 1].date : null);
  const holdDays = openDate && closeDate
    ? Math.max(1, Math.round((new Date(closeDate) - new Date(openDate)) / 86400000))
    : null;

  const pnlPct = trade.entry > 0 && avgExitQty > 0
    ? (totalPnl / (trade.entry * avgExitQty)) * 100
    : 0;

  return {
    ticker: trade.ticker,
    type: trade.type,
    side: trade.side || 'Long',
    currency: cur,
    entry: trade.entry,
    avgExit: +avgExit.toFixed(4),
    qty: trade._originalQty || trade.qty,
    leverage: trade.leverage || 1,
    sl: trade.sl || null,
    tp1: trade.tp1 || null,
    tp2: trade.tp2 || null,
    horizon: trade.horizon || 'short',
    pnlLocal: +related.reduce((s, r) => s + r.pnl, 0).toFixed(2),
    pnlUSD: +totalPnl.toFixed(2),
    pnlPct: +pnlPct.toFixed(2),
    holdDays,
    openDate: openDate || null,
    closeDate: closeDate || null,
    exitReasons: exits.map(e => promptSafe(e.reason, 80)).filter(Boolean),
    exitCount: exits.length,
    note: trade.note ? promptSafe(trade.note, 300) : null,
    win: totalPnl >= 0
  };
}

function _buildReviewMessages(ctx) {
  const system = `You are a professional trade journal analyst for SPEKTRX, a personal finance tracker.
Analyze this completed trade and provide a structured review.

Return JSON with this exact structure:
{
  "verdict": "WIN" | "LOSS" | "BREAKEVEN",
  "grade": "A" | "B" | "C" | "D" | "F",
  "summary": "one-sentence trade summary (max 100 chars)",
  "analysis": {
    "execution": "entry/exit timing quality assessment (max 150 chars)",
    "riskMgmt": "SL/TP discipline assessment (max 150 chars)",
    "sizing": "position sizing and leverage assessment (max 150 chars)"
  },
  "lessons": ["lesson 1 (max 80 chars)", "lesson 2"],
  "patterns": ["pattern observed (max 60 chars)"],
  "improvementTips": ["specific improvement tip (max 80 chars)"]
}

Grading criteria:
- A: Great execution, proper risk management, solid R:R, followed plan
- B: Good trade, minor issues in timing or sizing
- C: Average, some missed opportunities or loose stops
- D: Poor discipline, oversized, or no stop loss
- F: Catastrophic, revenge trade, or blown risk limits

Rules:
- Be specific to THIS trade data — no generic advice
- Reference actual entry/exit prices and P&L
- If leveraged, evaluate if leverage was appropriate for the setup
- If no SL was set, always flag it as a risk management issue
- Grade objectively — even profitable trades can get poor grades if execution was sloppy
- Max 2-3 lessons, max 2 patterns, max 2 tips`;

  const user = `Review this completed trade:\n\n${JSON.stringify(ctx, null, 2)}`;

  return [
    { role: 'system', content: system },
    { role: 'user', content: user }
  ];
}

/**
 * Generate AI review for a closed trade.
 */
export async function aiReviewTrade(tradeId) {
  const trade = S.trades.find(t => t.id === tradeId);
  if (!trade) { toast('Trade not found', 'a-r'); return; }
  if (trade.status !== 'Closed') { toast('Only closed trades can be reviewed', 'a-y'); return; }

  if (!isAIConfigured()) {
    toast('AI not configured. Go to Settings → AI.', 'a-r');
    return;
  }

  const now_ = Date.now();
  if (_loadingTradeId) { toast('Review already in progress...', 'a-y'); return; }
  if (now_ - _lastReviewTs < REVIEW_COOLDOWN_MS) {
    toast(`Please wait ${Math.ceil((REVIEW_COOLDOWN_MS - (now_ - _lastReviewTs)) / 1000)}s`, 'a-y');
    return;
  }

  _loadingTradeId = tradeId;
  _lastReviewTs = now_;

  // Show loading in the expanded journal area
  _expanded.add(tradeId);
  _renderLoading(tradeId);

  try {
    const ctx = _buildTradeContext(trade);
    const messages = _buildReviewMessages(ctx);
    const cfg = getAIConfig();

    const result = await chatJSON(messages, {
      maxTokens: 1500,
      temperature: 0.15,
      signal: AbortSignal.timeout(40000)
    });

    const data = result.data;
    if (!data?.verdict || !data?.summary) throw new Error('Invalid AI response structure');

    // Build journal entry from AI review
    const entry = {
      id: uid(),
      date: now(),
      type: 'ai_review',
      verdict: data.verdict,
      grade: data.grade || 'C',
      summary: String(data.summary || '').slice(0, 150),
      execution: String(data.analysis?.execution || '').slice(0, 200),
      riskMgmt: String(data.analysis?.riskMgmt || '').slice(0, 200),
      sizing: String(data.analysis?.sizing || '').slice(0, 200),
      lessons: (data.lessons || []).map(l => String(l).slice(0, 100)).slice(0, 3),
      patterns: (data.patterns || []).map(p => String(p).slice(0, 80)).slice(0, 3),
      tips: (data.improvementTips || []).map(t => String(t).slice(0, 100)).slice(0, 3),
      model: result.model || cfg.model,
      aiGenerated: true
    };

    if (!trade.journal) trade.journal = [];
    trade.journal.push(entry);
    stampItem(trade);
    save();

    _renderJournalArea(tradeId);
    toast(`AI review: ${data.verdict} (Grade ${data.grade})`, data.verdict === 'WIN' ? 'a-g' : 'a-y');

  } catch (e) {
    console.error('[AI Trade Journal] Error:', e);
    toast('AI review failed: ' + (e.message || 'Unknown error'), 'a-r');
    _renderJournalArea(tradeId);
  } finally {
    _loadingTradeId = null;
  }
}

// ─── MANUAL JOURNAL ENTRIES ───────────────────────────────────────

/**
 * Open inline editor for manual journal entry.
 */
export function addJournalEntry(tradeId) {
  _expanded.add(tradeId);
  const area = document.getElementById('tj-exp-' + tradeId);
  if (!area) return;

  // Show editor at top of expanded area
  const editor = document.createElement('div');
  editor.className = 'tj-editor';
  editor.id = 'tj-editor-' + tradeId;
  editor.innerHTML = `
    <textarea id="tj-note-${esc(tradeId)}" placeholder="What was your thesis? What did you learn?" style="width:100%;min-height:60px;background:rgba(8,12,24,.42);border:1px solid var(--bd);border-radius:6px;padding:8px;color:var(--t1);font-size:11px;resize:vertical;font-family:inherit"></textarea>
    <div style="display:flex;gap:6px;margin-top:6px;justify-content:flex-end">
      <button class="btn-xs" data-action="saveJournalEntry" data-args='["${esc(tradeId)}"]' style="font-size:10px;padding:3px 12px">Save</button>
      <button class="btn-xs" data-action="cancelJournalEdit" data-args='["${esc(tradeId)}"]' style="font-size:10px;padding:3px 8px;opacity:.6">Cancel</button>
    </div>`;

  // Insert at beginning
  const existing = document.getElementById('tj-editor-' + tradeId);
  if (existing) existing.remove();
  area.insertBefore(editor, area.firstChild);
  const ta = document.getElementById('tj-note-' + tradeId);
  if (ta) ta.focus();
}

/**
 * Save manual journal entry.
 */
export function saveJournalEntry(tradeId) {
  const trade = S.trades.find(t => t.id === tradeId);
  if (!trade) return;

  const ta = document.getElementById('tj-note-' + tradeId);
  const content = ta?.value?.trim();
  if (!content) { toast('Empty note', 'a-y'); return; }

  if (!trade.journal) trade.journal = [];
  trade.journal.push({
    id: uid(),
    date: now(),
    type: 'manual',
    content: content.slice(0, 1000),
    aiGenerated: false
  });
  stampItem(trade);
  save();

  _renderJournalArea(tradeId);
  toast('Journal entry saved', 'a-g');
}

/**
 * Cancel journal edit — remove editor.
 */
export function cancelJournalEdit(tradeId) {
  const editor = document.getElementById('tj-editor-' + tradeId);
  if (editor) editor.remove();
}

/**
 * Delete a journal entry.
 */
export function deleteJournalEntry(tradeId, entryId) {
  const trade = S.trades.find(t => t.id === tradeId);
  if (!trade?.journal) return;
  trade.journal = trade.journal.filter(e => e.id !== entryId);
  stampItem(trade);
  save();
  _renderJournalArea(tradeId);
  toast('Entry deleted', 'a-y');
}

// ─── EXPAND/COLLAPSE ──────────────────────────────────────────────

/**
 * Toggle journal section for a trade row.
 */
export function toggleJournalExpand(tradeId) {
  if (_expanded.has(tradeId)) {
    _expanded.delete(tradeId);
    const area = document.getElementById('tj-exp-' + tradeId);
    if (area) area.style.display = 'none';
  } else {
    _expanded.add(tradeId);
    _renderJournalArea(tradeId);
  }
}

// ─── RENDERING ────────────────────────────────────────────────────

function _renderLoading(tradeId) {
  const area = document.getElementById('tj-exp-' + tradeId);
  if (!area) return;
  area.style.display = 'block';
  area.innerHTML = `
    <div style="padding:12px;text-align:center">
      <div style="display:inline-block;width:16px;height:16px;border:2px solid var(--bd);border-top-color:var(--acc);border-radius:50%;animation:spin 1s linear infinite"></div>
      <div style="font-size:10px;color:var(--t3);margin-top:6px">Generating AI trade review...</div>
    </div>`;
}

function _renderJournalArea(tradeId) {
  const area = document.getElementById('tj-exp-' + tradeId);
  if (!area) return;
  area.style.display = 'block';

  const trade = S.trades.find(t => t.id === tradeId);
  if (!trade) return;

  const entries = trade.journal || [];
  const configured = isAIConfigured();
  const hasAiReview = entries.some(e => e.type === 'ai_review');

  let html = '';

  // Action buttons
  html += `<div style="display:flex;gap:6px;padding:6px 0;border-bottom:1px solid var(--bd);margin-bottom:6px">`;
  if (!hasAiReview && configured && trade.status === 'Closed') {
    html += `<button class="btn-xs" data-action="aiReviewTrade" data-args='["${esc(tradeId)}"]' style="font-size:9px;padding:3px 10px">AI Review</button>`;
  }
  html += `<button class="btn-xs" data-action="addJournalEntry" data-args='["${esc(tradeId)}"]' style="font-size:9px;padding:3px 10px">+ Note</button>`;
  html += `</div>`;

  if (!entries.length) {
    html += `<div style="padding:8px 0;font-size:10px;color:var(--t4);text-align:center">No journal entries yet</div>`;
  }

  // Render entries (newest first)
  [...entries].reverse().forEach(entry => {
    if (entry.type === 'ai_review') {
      html += _renderAIReview(entry, tradeId);
    } else {
      html += _renderManualEntry(entry, tradeId);
    }
  });

  area.innerHTML = html;
}

function _renderAIReview(entry, tradeId) {
  const gradeColors = { A: 'var(--grn)', B: '#7ff0c4', C: 'var(--yel)', D: 'var(--ora)', F: 'var(--red)' };
  const verdictColors = { WIN: 'var(--grn)', LOSS: 'var(--red)', BREAKEVEN: 'var(--yel)' };
  const gc = gradeColors[entry.grade] || 'var(--t3)';
  const vc = verdictColors[entry.verdict] || 'var(--t3)';
  const dt = new Date(entry.date).toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' });

  let html = `<div class="tj-entry tj-ai" style="padding:10px;margin:6px 0;background:rgba(114,38,255,.04);border-radius:8px;border-left:3px solid ${gc}">
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
      <div style="display:flex;align-items:center;gap:8px">
        <span style="font-size:16px;font-weight:700;color:${gc}">${esc(entry.grade)}</span>
        <span style="font-size:11px;font-weight:600;color:${vc}">${esc(entry.verdict)}</span>
        <span style="font-size:9px;padding:1px 6px;background:rgba(114,38,255,.1);border-radius:4px;color:var(--acc3)">AI Review</span>
      </div>
      <div style="display:flex;align-items:center;gap:6px">
        <span style="font-size:9px;color:var(--t4)">${dt}</span>
        <span style="font-size:9px;color:var(--t4);font-family:monospace">${esc(entry.model || '')}</span>
        <button style="background:none;border:none;cursor:pointer;color:var(--t4);font-size:11px;opacity:.5" data-action="deleteJournalEntry" data-args='["${esc(tradeId)}","${esc(entry.id)}"]' title="Delete">×</button>
      </div>
    </div>
    <div style="font-size:11px;color:var(--t1);margin-bottom:8px">${esc(entry.summary)}</div>`;

  // Analysis sections
  if (entry.execution) {
    html += `<div style="font-size:10px;margin-bottom:4px"><span style="color:var(--t3);font-weight:600">Execution:</span> <span style="color:var(--t2)">${esc(entry.execution)}</span></div>`;
  }
  if (entry.riskMgmt) {
    html += `<div style="font-size:10px;margin-bottom:4px"><span style="color:var(--t3);font-weight:600">Risk Mgmt:</span> <span style="color:var(--t2)">${esc(entry.riskMgmt)}</span></div>`;
  }
  if (entry.sizing) {
    html += `<div style="font-size:10px;margin-bottom:4px"><span style="color:var(--t3);font-weight:600">Sizing:</span> <span style="color:var(--t2)">${esc(entry.sizing)}</span></div>`;
  }

  // Lessons
  if (entry.lessons?.length) {
    html += `<div style="margin-top:6px;font-size:10px;color:var(--t3);font-weight:600">Lessons:</div>`;
    entry.lessons.forEach(l => {
      html += `<div style="font-size:10px;color:var(--t2);padding-left:8px">• ${esc(l)}</div>`;
    });
  }

  // Patterns
  if (entry.patterns?.length) {
    html += `<div style="margin-top:4px;font-size:10px;color:var(--t3);font-weight:600">Patterns:</div>`;
    entry.patterns.forEach(p => {
      html += `<div style="font-size:10px;color:var(--t2);padding-left:8px">• ${esc(p)}</div>`;
    });
  }

  // Tips
  if (entry.tips?.length) {
    html += `<div style="margin-top:4px;font-size:10px;color:var(--t3);font-weight:600">Next time:</div>`;
    entry.tips.forEach(t => {
      html += `<div style="font-size:10px;color:var(--acc3);padding-left:8px">→ ${esc(t)}</div>`;
    });
  }

  html += `</div>`;
  return html;
}

function _renderManualEntry(entry, tradeId) {
  const dt = new Date(entry.date).toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' });
  return `<div class="tj-entry tj-manual gls-soft" style="padding:8px 10px;margin:4px 0;border:1px solid var(--bd);border-radius:6px">
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px">
      <span style="font-size:9px;color:var(--t4)">${dt}</span>
      <button style="background:none;border:none;cursor:pointer;color:var(--t4);font-size:11px;opacity:.5" data-action="deleteJournalEntry" data-args='["${esc(tradeId)}","${esc(entry.id)}"]' title="Delete">×</button>
    </div>
    <div style="font-size:11px;color:var(--t2);line-height:1.4;white-space:pre-wrap">${esc(entry.content || '')}</div>
  </div>`;
}

// ─── INTEGRATION HELPERS ──────────────────────────────────────────

/**
 * Returns badge HTML if trade has journal entries (used by trades.js render).
 */
export function getJournalBadge(trade) {
  const count = (trade.journal || []).length;
  if (!count) return '';
  const hasAI = trade.journal.some(e => e.type === 'ai_review');
  return `<span style="font-size:8px;padding:1px 5px;background:${hasAI ? 'rgba(114,38,255,.1)' : 'var(--bg0)'};border-radius:3px;color:${hasAI ? 'var(--acc3)' : 'var(--t3)'};margin-left:4px">${count}📝</span>`;
}

/**
 * Returns the expandable area HTML for a trade row in the journal table.
 * Called by renderTradeJournal to inject expand targets.
 */
export function getJournalExpandRow(tradeId, colSpan) {
  const isExpanded = _expanded.has(tradeId);
  return `<tr class="tj-expand-row"><td colspan="${colSpan}" style="padding:0;border:none">
    <div id="tj-exp-${esc(tradeId)}" style="display:${isExpanded ? 'block' : 'none'};padding:4px 12px 8px"></div>
  </td></tr>`;
}

/**
 * Show toast offering AI review after trade close/exit.
 * Called from execExit when trade becomes fully closed.
 */
export function offerPostExitReview(trade) {
  if (!isAIConfigured()) return;
  if (!trade || trade.status !== 'Closed') return;
  // Slight delay so the exit flow completes first
  setTimeout(() => {
    toast(
      `Trade ${esc(trade.ticker)} closed. <button class="btn-xs" data-action="aiReviewTrade" data-args='["${esc(trade.id)}"]' style="font-size:9px;padding:2px 8px;margin-left:6px">AI Review</button>`,
      'a-b',
      8000
    );
  }, 500);
}

/**
 * Trigger rendering of journal area if already expanded (called after page render).
 */
export function refreshExpandedJournals() {
  _expanded.forEach(tradeId => {
    const area = document.getElementById('tj-exp-' + tradeId);
    if (area) _renderJournalArea(tradeId);
  });
}
